/*@flow*/
import type { StateT, DistrictT } from './types';
import getDisabled from './getDisabled';
import dasherize from 'underscore.string/dasherize';
import downloadCSV from 'lib/downloadCSV';
import { refetchQueries } from './queries';
import toast from 'lib/toast';
import { set, state, q } from 'ops';
import PATHS from 'const/paths';
import { DEFAULT_STROKE_COLOR } from './constants';
import * as I from 'immutable';
import flash from 'lib/flash';
import NonEmptyArray from 'lib/NonEmptyArray';
import parseError from './parseError';

function drawingGeoJSON(): ?{} {
  const handler = state(PATHS.drawHandler);
  if (handler && handler.isValid()) return handler.polygon.toGeoJSON();
}

const commands = (ctx: any, cmd: any) => {
  const isDisabled = getDisabled(ctx.state);

  return {
    editPolygon() {
      if (isDisabled('EDIT_AREA_BUTTON')) return;
      const { geojson } = ctx.state.areaWorkingCopy;
      commands(ctx, cmd).loadDrawing(geojson);
      commands(ctx, cmd).enableDrawing();

      // Change component to drawing mode
      commands(ctx, cmd).changeMode('DRAWING');

      // Close the Edit dialogue
      commands(ctx, cmd).setEditOverlay(false);
    },
    removePolygon() {
      set((v) => v.setIn(PATHS.areaPolygonGeoJSON.split('.'), null));
      cmd.remove('decks.AreaPolygon');
      cmd.remove('decks.drawPoly.geojson');
    },
    loadPolygon(geojson: {}, color: ?string = DEFAULT_STROKE_COLOR) {
      set((v) =>
        v.setIn(PATHS.areaPolygonGeoJSON.split('.'), JSON.stringify(geojson))
      );

      const config = I.fromJS({
        type: 'polygon',
        geojson,
        active: true,
        stroke: true,
        fillColor: '#ababab85',
        color,
        weight: 3,
        opacity: 0.8,
        fillOpacity: 0.2,
        fill: true,
        clickable: false,
        className: 'area-polygon',
      });

      cmd.set('decks.AreaPolygon', config);
    },
    reloadPolygon(geojson: {}, color: ?string = DEFAULT_STROKE_COLOR) {
      commands(ctx, cmd).removePolygon();
      commands(ctx, cmd).loadPolygon(geojson, color);
    },

    editName(v: ?string) {
      commands(ctx, cmd).set('areaWorkingCopy.name', v);
    },
    editColor(v: ?string) {
      commands(ctx, cmd).set('areaWorkingCopy.color', v);
    },
    saveBackup() {
      const v = ctx.state.areaWorkingCopy;
      commands(ctx, cmd).set('areaBackup', v);
    },
    areaSaved(v: DistrictT) {
      ctx.dispatch({
        type: 'AREA_SAVED',
        payload: v,
      });
    },
    mapShared(v: DistrictT) {
      ctx.dispatch({
        type: 'MAP_SHARED',
        payload: v,
      });
    },
    cloneArea() {
      if (isDisabled('CLONE_AREA_BUTTON')) return;
      ctx.dispatch({
        type: 'AREA_CLONED',
      });
    },
    downloadShape() {
      const { name, geojson } = ctx.state.areaWorkingCopy;
      const safeName = dasherize(name);
      const esc = (str) => str.replace(/"/g, '""');
      downloadCSV({
        data: [`geojson\n"${esc(geojson)}"`],
        fileName: `${safeName}.csv`,
      });
    },
    showEditOverlay() {
      commands(ctx, cmd).disableDrawing();
      const fn = () => commands(ctx, cmd).setEditOverlay(true);
      // HACK: If the overlay is open, and about to be closed, we
      // should wait for it to close. (The overlay is closed automatically
      // when the user clicks outside the container of overlay.)
      if (ctx.state.showEditOverlay) {
        setTimeout(fn, 300);
      } else {
        fn();
      }
    },
    deletionRequested(del: Function) {
      if (isDisabled('DELETE_AREA_BUTTON')) return;
      const id = ctx.state.areaWorkingCopy.id;

      del({
        variables: { input: { id } },
        refetchQueries,
      }).then((res) => {
        const keyPath = ['data', 'districtDestroy'];
        const { success, errors } = I.getIn(res, keyPath, {});
        if (success) {
          toast('Area deleted.');
        } else if (errors) {
          commands(ctx, cmd).serverErrors(errors);
        }
        commands(ctx, cmd).clearArea();
      });
    },
    serverErrors(errs: string[]) {
      commands(ctx, cmd).set('serverErrors', errs);
    },
    startDrawing() {
      commands(ctx, cmd).enableDrawing();
      cmd.toggleParcelLayers(true);
      commands(ctx, cmd).changeMode('DRAWING');
    },
    loadDrawing(geojson: Object) {
      cmd.set('decks.drawPoly.geojson', geojson);
    },
    enableDrawing() {
      set(PATHS.areaPolygonModeType, 'Drawing');
      cmd.set('decks.drawPoly.active', true);
      const deactivate = (v) => (v ? v.set('active', false) : v);
      cmd.set('decks.AreaPolygon', deactivate);
      cmd.remove('decks.marker');
    },
    disableDrawing() {
      set(PATHS.areaPolygonModeType, 'Ready');
      // DEPRECATE
      cmd.set('decks.drawPoly.active', false);
      // DEPRECATE
      cmd.remove('decks.drawPoly.geojson');
      const activate = (v) => (v ? v.set('active', true) : v);
      // DEPRECATE
      cmd.set('decks.AreaPolygon', activate);
    },
    cancel() {
      commands(ctx, cmd).clearArea();
      commands(ctx, cmd).disableDrawing();

      commands(ctx, cmd).removePolygon();

      cmd.toggleParcelLayers(false);
      ctx.dispatch({
        type: 'DRAWING_CANCELLED',
      });
    },
    changeMode(name: $PropertyType<StateT, 'mode'>) {
      ctx.dispatch({
        type: 'MODE_CHANGE',
        payload: name,
      });
    },
    finish(save: Function) {
      // If the working copy has an id, apply drawing and save
      // district
      const geojson = drawingGeoJSON();
      if (geojson) {
        const { areaWorkingCopy } = ctx.state;

        // Add geojson to working copy
        const geoStr = JSON.stringify(geojson);
        commands(ctx, cmd).set('areaWorkingCopy.geojson', geoStr);
        const { id, color } = areaWorkingCopy;
        commands(ctx, cmd).loadPolygon(geojson, color);
        commands(ctx, cmd).changeMode('READY');

        if (id) {
          // Save the district
          save({ variables: areaWorkingCopy.set('geojson', geoStr).toJS() })
            .then((res) => {
              if (res.success) {
                toast('Area created.');
              } else if (NonEmptyArray.is(res.errors)) {
                flash(res.errors.join('\n'));
              }
            })
            .catch((err) => {
              const errors = parseError(err);
              flash(errors.join('\n'));
            });
        }

        commands(ctx, cmd).disableDrawing();
      } else {
        // TODO: shake the done button left to right to indicate
        // a problem.
      }
    },
    setEditOverlay(v: Function | boolean) {
      commands(ctx, cmd).set('showEditOverlay', v);
    },
    selectArea(district: $Shape<DistrictT>) {
      const { id, color } = district;

      // Load new polygon
      const geojson = JSON.parse(district.geojson);
      commands(ctx, cmd).loadPolygon(geojson, color);

      // Add areaId to query string
      q((v) => v.set('areaId', id));

      // Close out left sidebar
      cmd.closePanel();

      ctx.dispatch({
        type: 'AREA_SELECTED',
        payload: district,
      });
    },
    clearArea() {
      commands(ctx, cmd).setEditOverlay(false);
      setTimeout(() => {
        commands(ctx, cmd).removePolygon();

        // Remove areaId from query string
        q((v) => v.delete('areaId').deleteIn(['filter', 'areaId']));

        ctx.dispatch({
          type: 'AREA_CLEARED',
        });
      }, 50);
    },
    set(path: string, value: any) {
      ctx.dispatch({
        type: 'SET_RECORD',
        payload: { path, value },
      });
    },
  };
};

export default commands;
